import React, { useContext, useEffect, useState, useCallback, useMemo } from 'react';
import { IAppContext, AppContext } from 'appState/appContext';
import { IItemForGetShortInfo, UserRoles, IGetRestaurantsShort, IGetRestaurantsShortArgs } from 'types';
import { getRestaurantsShortInfo } from 'backend/restaurants';
import { useRoleAccess } from 'hooks/useRoleAccess';
import { useFetchData } from 'hooks/useFetchData';
import MultipleSelect from './components/MultipleSelect';
import SelectedItem from 'components/SelectedItem/SelectedItem';
import style from './RestaurantsMultipleSelect.module.scss';

interface IRestaurantsMultipleSelect {
	setIDsRestaurant: (value: string[]) => void;
	setFetchedRestaurants: (value: IItemForGetShortInfo[]) => void;
	changeLoaderStatus: (value: boolean) => void;
	IDSelectedCustomer: Nullable<string>;
	initialIDsSelectedRestaurant: string[];
	IDsSelectedRestaurant: string[];
}

const RestaurantsMultipleSelect: React.FC<IRestaurantsMultipleSelect> = ({
	setIDsRestaurant,
	setFetchedRestaurants,
	changeLoaderStatus,
	IDSelectedCustomer,
	initialIDsSelectedRestaurant,
	IDsSelectedRestaurant,
}) => {
	const { loggedUser } = useContext<IAppContext>(AppContext);
	const hasAccess = useRoleAccess([
		UserRoles.SUPER_USER,
		UserRoles.CLIENT_MANAGER,
		UserRoles.GROUP_MANAGER,
		UserRoles.TECHNICAL,
	]);
	const { response, loading, refetch, responseError } = useFetchData<IGetRestaurantsShort, IGetRestaurantsShortArgs>(
		IDSelectedCustomer ? getRestaurantsShortInfo : null,
		{
			customerId: IDSelectedCustomer || '',
			menuApiVersion: 'v1',
		},
	);
	const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

	useEffect(() => {
		refetch();
	}, [IDSelectedCustomer]);

	const handleRemove = useCallback(
		(id: string) => {
			const updatedSelectedItems = IDsSelectedRestaurant.filter((el) => el !== id);
			setIDsRestaurant(updatedSelectedItems);
		},
		[setIDsRestaurant, IDsSelectedRestaurant],
	);

	const restaurantsList = useMemo(() => {
		return IDsSelectedRestaurant.map((el) => {
			const label = response?.data.restaurants.find((item) => item.id === el)?.name || '';
			const accessibleRestaurantIds = loggedUser.restaurants?.map((el) => el.id);
			return (
				<SelectedItem
					key={el}
					label={label}
					onDelete={!hasAccess && !accessibleRestaurantIds?.includes(el) ? undefined : () => handleRemove(el)}
				/>
			);
		});
	}, [IDsSelectedRestaurant, response, loggedUser, hasAccess, handleRemove]);

	useEffect(() => {
		if (response) {
			const accessibleRestaurants = loggedUser.restaurants ? loggedUser.restaurants : [];
			const restaurants = hasAccess ? response.data.restaurants : accessibleRestaurants;
			setFetchedRestaurants(restaurants);
		}
		if (isFirstRender && response) {
			setIDsRestaurant(initialIDsSelectedRestaurant);
			changeLoaderStatus(false);
			setIsFirstRender(false);
		}
	}, [response, loggedUser, hasAccess, initialIDsSelectedRestaurant]);

	return (
		<div className={style.container} data-test="RestaurantsMultipleSelect">
			<MultipleSelect
				data={response?.data.restaurants || []}
				isLoading={loading || isFirstRender}
				refetch={refetch}
				responseError={responseError}
				setIDsRestaurant={setIDsRestaurant}
				IDSelectedCustomer={IDSelectedCustomer}
				IDsSelectedRestaurant={IDsSelectedRestaurant}
			/>
			<div className={style.groupsList}>{restaurantsList}</div>
		</div>
	);
};

export default React.memo(RestaurantsMultipleSelect);
